import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import AuthLayout from "@/pages/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [twofa, setTwofa] = useState(null); // pre_auth_token
  const [code, setCode] = useState("");

  const finish = (res) => {
    if (res.must_reset_password) {
      toast.info("Please set a new password to continue");
      navigate("/force-reset");
    } else {
      navigate("/dashboard");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(identifier.trim(), password);
      if (res.requires_2fa) {
        setTwofa(res.pre_auth_token);
        toast.info("Enter your authenticator code");
      } else {
        finish(res);
      }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await verify2fa(twofa, code.trim());
      finish(res);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (twofa) {
    return (
      <AuthLayout title="Two-factor authentication" subtitle="Enter the 6-digit code from your authenticator app.">
        <form onSubmit={submit2fa} className="space-y-5">
          <div>
            <Label htmlFor="code">Authentication code</Label>
            <Input id="code" inputMode="numeric" maxLength={6} value={code} data-testid="twofa-code-input"
              onChange={(e) => setCode(e.target.value)} placeholder="000000"
              className="mt-1.5 h-12 text-center text-lg tracking-[0.5em]" autoFocus />
          </div>
          {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="twofa-submit-btn" className="w-full h-12 bg-teal hover:bg-teal-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
          </Button>
          <button type="button" onClick={() => setTwofa(null)} className="text-sm text-slate-500 hover:text-teal w-full">Back to login</button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in with your Medoho Sales OS account.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label htmlFor="identifier">Username or Email</Label>
          <Input id="identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
            data-testid="login-identifier-input" placeholder="e.g. arjun or you@medoho.com"
            className="mt-1.5 h-12" autoFocus autoComplete="username" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-teal hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <div className="relative mt-1.5">
            <Input id="password" type={show ? "text" : "password"} value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
              className="h-12 pr-11" autoComplete="current-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-12 bg-teal hover:bg-teal-700 text-base">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
        <p className="text-xs text-center text-slate-400">
          No public sign-up. Accounts are created by your administrator.
        </p>
      </form>
    </AuthLayout>
  );
}
